import React, { createContext, ReactNode, useContext, useMemo } from 'react';

import { useCompanyActivationTokenValidate } from '@/core/domain/enterprise/Companies/Activation/Activation.hooks';
import {
  ActivationBlockReason,
  CompanyActivationTokenValidateResponse,
} from '@/core/domain/enterprise/Companies/Activation/Activation.types';
import {
  getActivationBlockReason,
  getActivationTokenBlockReason,
  isActivationTokenAvailable,
} from '@/core/domain/enterprise/Companies/Activation/Activation.utils';
import { useRouteMatchParam } from '@/hooks';

type ActivationHashPageProviderProps = {
  children: ReactNode;
};

type ActivationHashPageContextValue = {
  activationToken?: string;
  blockReason?: ActivationBlockReason;
  isTokenLoading: boolean;
  isTokenValid: boolean;
  refetchToken: () => void;
  tokenData?: CompanyActivationTokenValidateResponse;
  validationFinished: boolean;
};

const ActivationHashPageContext = createContext(
  {} as ActivationHashPageContextValue,
);
export const useActivationHashPage = () =>
  useContext(ActivationHashPageContext);

export default function ActivationHashPageProvider({
  children,
}: ActivationHashPageProviderProps) {
  const activationToken = useRouteMatchParam('activation_token');

  const tokenQuery = useCompanyActivationTokenValidate(activationToken);

  const tokenData = tokenQuery.data;
  const validationFinished = !tokenQuery.isLoading && !tokenQuery.isIdle;
  const isTokenValid =
    !!activationToken && isActivationTokenAvailable(tokenData);

  const blockReason = !activationToken
    ? ActivationBlockReason.Invalid
    : tokenQuery.isError
    ? getActivationBlockReason(tokenQuery.error)
    : validationFinished && tokenData && !isTokenValid
    ? getActivationTokenBlockReason(tokenData)
    : undefined;

  const value = useMemo(
    () => ({
      activationToken,
      blockReason,
      isTokenLoading: tokenQuery.isLoading,
      isTokenValid,
      refetchToken: tokenQuery.refetch,
      tokenData,
      validationFinished,
    }),
    [
      activationToken,
      blockReason,
      isTokenValid,
      tokenData,
      tokenQuery.isLoading,
      tokenQuery.refetch,
      validationFinished,
    ],
  );

  return (
    <ActivationHashPageContext.Provider value={value}>
      {children}
    </ActivationHashPageContext.Provider>
  );
}
